/**
 * The composer's one control for the model and how it runs, after upstream
 * T3 Code #28057.
 * The popup lists every instance's models in one list, a section per
 * instance, then a row each for the selected model's Reasoning and Speed and
 * for the chat's Access. Up/Down walks the models, then the rows; picking a
 * model keeps it open. The model-picker jump shortcuts (Mod+1-9 by default)
 * pick the first nine selectable models.
 *
 * @module components/chat/ModelEffortPicker
 */
import type {
  ProviderDriverKind,
  ProviderInstanceId,
  ProviderOptionSelection,
  ScopedThreadRef,
  ServerProviderModel,
} from "@amoreg/contracts";
import { buildProviderOptionSelectionsFromDescriptors, getProviderOptionDescriptors, resolveSelectableModel } from "@amoreg/shared/model";
import { type ComponentProps, type KeyboardEvent, Fragment, memo, useEffect, useRef } from "react";

import { cn } from "~/lib/utils";
import { type DraftId, useComposerDraftStore } from "../../composerDraftStore";
import {
  modelPickerJumpCommandForIndex,
  modelPickerJumpIndexFromCommand,
  resolveShortcutCommand,
  shortcutLabelForCommand,
} from "../../keybindings";
import { rememberModelEffortPreference } from "../../modelEffortPreferences";
import { getProviderModelCapabilities } from "../../providerModels";
import { DEFAULT_TOOL_ACCESS_MODE } from "../../toolAccess";
import { Combobox, ComboboxGroup, ComboboxGroupLabel, ComboboxListVirtualized, ComboboxSeparator } from "../ui/combobox";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { TooltipProvider } from "../ui/tooltip";
import { type ComposerAccessMode, composerAccessOptions, isComposerAccessMode } from "./ComposerAccessControl";
import { ComposerControl } from "./ComposerControl";
import { composerFloatingLayerProps } from "./composerEventScope";
import {
  buildModelSections,
  effortSelects,
  reasoningSegments,
  selectValue,
  speedSegments,
  type SelectDescriptor,
} from "./ModelEffortPicker.logic";
import { ModelEffortSegmented, type Segment } from "./ModelEffortSegmented";
import { ModelEffortSettingRow } from "./ModelEffortSettingRow";
import { ModelEffortSpeedIcon } from "./ModelEffortSpeedIcon";
import { ModelListRow } from "./ModelListRow";
import { modelPickerModelKey, parseModelPickerModelKey } from "./modelPickerKeys";
import { isModelPickerNewModel } from "./modelPickerModelHighlights";
import { useOptionConfirmationStore } from "./optionConfirmationStore";
import type { ProviderModelPicker } from "./ProviderModelPicker";
import { ProviderInstanceIcon } from "./ProviderInstanceIcon";
import { getTriggerDisplayModelName } from "./providerIconUtils";
import { buildTraitsTriggerDisplay } from "./TraitsPicker.logic";

/** The model list and its trigger state, in the full model picker's own props. */
export type ModelEffortModelProps = ComponentProps<typeof ProviderModelPicker>;

/** The selected model's options, and the thread or draft its choices are saved to. */
export interface ModelEffortTraits {
  readonly provider: ProviderDriverKind;
  readonly instanceId?: ProviderInstanceId;
  readonly threadRef?: ScopedThreadRef;
  readonly draftId?: DraftId;
  readonly model: string;
  readonly models: ReadonlyArray<ServerProviderModel>;
  readonly modelOptions: ReadonlyArray<ProviderOptionSelection> | undefined;
}

export interface ModelEffortAccess {
  readonly mode: ComposerAccessMode;
  readonly onChange: (mode: ComposerAccessMode) => void;
  readonly disabled: boolean;
}

const ACCESS_SEGMENTS: ReadonlyArray<Segment> = Object.entries(composerAccessOptions).map(([id, option]) => {
  const Icon = option.icon;
  return { id, label: option.label, title: option.description, icon: <Icon aria-hidden="true" className="size-3 shrink-0" /> };
});

const noop = () => {};

/** The chat's tool access, as the picker's last row. */
export function ModelEffortAccessRow({ access }: { readonly access: ModelEffortAccess }) {
  return (
    <ModelEffortSettingRow label="Access">
      <ModelEffortSegmented
        label="Access"
        segments={ACCESS_SEGMENTS}
        value={access.mode}
        disabled={access.disabled}
        onChange={(id) => {
          if (isComposerAccessMode(id)) access.onChange(id);
        }}
      />
    </ModelEffortSettingRow>
  );
}

const MAX_JUMP_TARGETS = 9;

export const ModelEffortPicker = memo(function ModelEffortPicker(props: {
  readonly picker: ModelEffortModelProps;
  readonly traits: ModelEffortTraits;
  /** The chat's tool access, as two segments; null when the composer offers no choice. */
  readonly access: ModelEffortAccess | null;
}) {
  const { picker, traits } = props;
  const popupRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightedIndexRef = useRef(-1);
  const focusEffortOnOpenRef = useRef(false);
  const setProviderModelOptions = useComposerDraftStore((store) => store.setProviderModelOptions);
  const requestConfirmation = useOptionConfirmationStore((store) => store.request);

  const isOpen = picker.open ?? false;
  const setOpen = (open: boolean) => picker.onOpenChange?.(open);

  const activeEntry = picker.instanceEntries.find((entry) => entry.instanceId === picker.activeInstanceId) ?? null;
  const activeOptions = picker.modelOptionsByInstance.get(picker.activeInstanceId) ?? [];
  const activeModel = activeOptions.find((option) => option.slug === picker.model) ?? activeOptions[0] ?? null;
  const modelName = activeModel ? getTriggerDisplayModelName(activeModel) : picker.model;
  const selectedKey = modelPickerModelKey(picker.activeInstanceId, picker.model);
  const sections = buildModelSections(picker);
  const itemKeys = sections.flatMap((section) => section.rows.map((row) => row.key));
  const jumpKeys = sections
    .flatMap((section) => section.rows.filter((row) => row.disabledReason === null).map((row) => row.key))
    .slice(0, MAX_JUMP_TARGETS);
  const jumpShortcutOptions = {
    context: { terminalFocus: false, terminalOpen: picker.terminalOpen ?? false, modelPickerOpen: true },
  };
  const keybindings = picker.keybindings;
  const jumpLabels = new Map(
    isOpen && keybindings
      ? jumpKeys.flatMap((key, index) => {
          const command = modelPickerJumpCommandForIndex(index);
          const label = command ? shortcutLabelForCommand(keybindings, command, jumpShortcutOptions) : null;
          return label ? [[key, label] as const] : [];
        })
      : [],
  );

  const selectModel = (key: unknown) => {
    const parsed = typeof key === "string" ? parseModelPickerModelKey(key) : null;
    if (!parsed || picker.disabled) return;
    const entry = picker.instanceEntries.find((item) => item.instanceId === parsed.instanceId);
    const options = picker.modelOptionsByInstance.get(parsed.instanceId);
    if (!entry || !options || picker.getModelDisabledReason?.(parsed.instanceId, parsed.slug)) return;
    const model = resolveSelectableModel(entry.driverKind, parsed.slug, options);
    if (model) picker.onInstanceModelChange(parsed.instanceId, model);
  };

  const descriptors = getProviderOptionDescriptors({
    caps: getProviderModelCapabilities(traits.models, traits.model, traits.provider),
    selections: traits.modelOptions,
  });
  const { reasoning, speed } = effortSelects(descriptors);
  const triggerTraits = buildTraitsTriggerDisplay({
    provider: traits.provider,
    descriptors,
    primarySelectDescriptorId: reasoning?.id ?? null,
    ultrathinkPromptControlled: false,
  });
  const persistTarget = traits.threadRef ?? traits.draftId;
  const access = props.access ? composerAccessOptions[props.access.mode] : null;
  const AccessIcon = props.access && props.access.mode !== DEFAULT_TOOL_ACCESS_MODE ? access?.icon : undefined;
  const triggerLabel = [modelName, triggerTraits.accessibleLabel, access ? `Access: ${access.label}` : null]
    .filter(Boolean)
    .join(", ");

  const changeSelect = (descriptor: SelectDescriptor, value: string) => {
    if (!persistTarget || value === selectValue(descriptor)) return;
    const apply = () => {
      const next = descriptors.map((entry) =>
        entry.id === descriptor.id && entry.type === "select" ? { ...entry, currentValue: value } : entry,
      );
      setProviderModelOptions(persistTarget, traits.provider, buildProviderOptionSelectionsFromDescriptors(next), {
        ...(traits.instanceId ? { instanceId: traits.instanceId } : {}),
        model: traits.model,
        persistSticky: true,
      });
      if (descriptor === reasoning && traits.instanceId) {
        rememberModelEffortPreference(traits.instanceId, traits.model, value);
      }
    };
    const confirmation = descriptor.options.find((option) => option.id === value)?.confirmation;
    if (confirmation) {
      requestConfirmation(confirmation, apply);
      return;
    }
    apply();
  };

  const selectRow = (descriptor: SelectDescriptor, segments: ReadonlyArray<Segment>) => (
    <ModelEffortSegmented
      label={descriptor.label}
      segments={segments}
      value={selectValue(descriptor)}
      disabled={!persistTarget}
      onChange={(value) => changeSelect(descriptor, value)}
    />
  );

  const focusEffort = () =>
    popupRef.current?.querySelector<HTMLButtonElement>('[data-effort-option][aria-checked="true"]')?.focus();

  // The popup's initial focus lands on the list first, so the effort shortcut moves it after.
  useEffect(() => {
    if (!isOpen || !focusEffortOnOpenRef.current) return;
    focusEffortOnOpenRef.current = false;
    const timeout = window.setTimeout(focusEffort, 50);
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  const openOnEffort = () => {
    if (picker.disabled) return;
    if (isOpen) {
      focusEffort();
      return;
    }
    focusEffortOnOpenRef.current = true;
    setOpen(true);
  };

  // Capture phase, as the full model picker does: Mod+1 is also a thread jump, which must not run under the open picker.
  useEffect(() => {
    if (!isOpen || !keybindings) return;
    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const index = modelPickerJumpIndexFromCommand(resolveShortcutCommand(event, keybindings, jumpShortcutOptions) ?? "");
      const key = index === null ? undefined : jumpKeys[index];
      if (!key) return;
      event.preventDefault();
      event.stopPropagation();
      selectModel(key);
    };
    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => window.removeEventListener("keydown", onWindowKeyDown, true);
  });

  const settingRows = () => Array.from(popupRef.current?.querySelectorAll<HTMLElement>("[data-model-effort-row]") ?? []);
  const focusRow = (row: HTMLElement | undefined) =>
    row?.querySelector<HTMLElement>('[role=radio][aria-checked="true"]')?.focus();

  const onListKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown") return;
    if (highlightedIndexRef.current !== itemKeys.length - 1) return;
    const first = settingRows()[0];
    if (!first) return;
    event.preventDefault();
    event.stopPropagation();
    focusRow(first);
  };

  /** Up/Down between the setting rows; Up from the first goes back to the list. */
  const onRowsKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    const rows = settingRows();
    const index = rows.findIndex((row) => row.contains(event.target as Node));
    if (index < 0) return;
    const step = event.key === "ArrowDown" ? 1 : -1;
    if (index + step >= rows.length) return;
    event.preventDefault();
    event.stopPropagation();
    if (index + step < 0) listRef.current?.focus();
    else focusRow(rows[index + step]);
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={(open) => setOpen(picker.disabled ? false : open)}>
        <PopoverTrigger
          render={
            <ComposerControl
              variant="ghost"
              size="sm"
              data-chat-provider-model-picker="true"
              aria-label={triggerLabel}
              disabled={picker.disabled}
              className="group/model-effort min-w-0 shrink gap-1.5 whitespace-nowrap ps-1.5 pe-2 [:hover,[data-pressed]]:bg-foreground/[0.07] data-popup-open:bg-foreground/[0.07]"
            />
          }
        >
          {activeEntry ? (
            <ProviderInstanceIcon
              driverKind={activeEntry.driverKind}
              displayName={activeEntry.displayName}
              accentColor={activeEntry.accentColor}
              className="size-3.5"
              iconClassName={cn("size-3.5", picker.activeProviderIconClassName)}
              indicatorBackground="var(--input)"
            />
          ) : null}
          <span className="min-w-0 truncate" data-chat-provider-model-picker-label="true">
            {modelName}
          </span>
          {triggerTraits.label || triggerTraits.speed ? (
            <span className="flex shrink-0 items-center gap-1 text-muted-foreground group-hover/model-effort:text-foreground group-data-popup-open/model-effort:text-foreground">
              {/* On a phone the row keeps the model's name; the trigger's label still names the level. */}
              {triggerTraits.label ? <span className="@max-[30rem]/composer-surface:hidden">{triggerTraits.label}</span> : null}
              {triggerTraits.speed ? (
                <ModelEffortSpeedIcon tier={triggerTraits.speed} fine className="opacity-80" />
              ) : null}
            </span>
          ) : null}
          {AccessIcon ? (
            <AccessIcon
              aria-hidden="true"
              data-model-effort-access-icon=""
              className="size-3.5 shrink-0 stroke-[1.75] text-muted-foreground group-hover/model-effort:text-foreground group-data-popup-open/model-effort:text-foreground"
            />
          ) : null}
        </PopoverTrigger>
        <PopoverPopup
          {...composerFloatingLayerProps}
          ref={popupRef}
          align="end"
          initialFocus={listRef}
          bare
          viewportClassName="rounded-lg p-0"
        >
          <TooltipProvider delay={0}>
            <div
              data-model-effort-popup=""
              className="dropdown-glass model-picker-surface flex max-h-(--available-height) w-screen max-w-[24rem] flex-col overflow-hidden rounded-lg text-popover-foreground shadow-[0_18px_44px_-18px_rgb(0_0_0/0.8)]"
            >
              {/* A long model list scrolls; Reasoning, Speed and Access stay pinned below it. */}
              <div className="flex min-h-0 flex-col bg-muted/40">
                <Combobox
                  inline
                  open
                  items={itemKeys}
                  filter={null}
                  value={selectedKey}
                  onValueChange={selectModel}
                  onItemHighlighted={(_key, details) => {
                    highlightedIndexRef.current = details.index;
                  }}
                >
                  <ComboboxListVirtualized
                    ref={listRef}
                    aria-label="Model"
                    className="min-h-0 overflow-y-auto overscroll-contain p-1.5 not-empty:p-1.5 outline-none"
                    onKeyDownCapture={onListKeyDownCapture}
                  >
                    {sections.map((section, sectionIndex) => (
                      <Fragment key={section.entry.instanceId}>
                        {sectionIndex > 0 ? <ComboboxSeparator className="mx-1 my-1.5" /> : null}
                        <ComboboxGroup>
                          <ComboboxGroupLabel className="flex items-center gap-1.5 px-2 pt-1 pb-0.5 text-[11px] text-muted-foreground/70">
                            <ProviderInstanceIcon
                              driverKind={section.entry.driverKind}
                              displayName={section.entry.displayName}
                              accentColor={section.entry.accentColor}
                              className="size-3.5"
                              iconClassName="size-3.5"
                            />
                            {section.entry.displayName}
                          </ComboboxGroupLabel>
                          {section.rows.map((row) => (
                            <ModelListRow
                              key={row.key}
                              index={row.index}
                              model={row.model}
                              instanceId={section.entry.instanceId}
                              driverKind={section.entry.driverKind}
                              providerDisplayName={section.entry.displayName}
                              providerAccentColor={section.entry.accentColor}
                              isFavorite={false}
                              isSelected={row.key === selectedKey}
                              showProvider={Boolean(row.model.subProvider)}
                              preferShortName={picker.lockedProvider === null}
                              showNewBadge={isModelPickerNewModel(section.entry.driverKind, row.model.slug)}
                              jumpLabel={jumpLabels.get(row.key) ?? null}
                              disabledReason={row.disabledReason}
                              onToggleFavorite={noop}
                              hideFavorite
                            />
                          ))}
                        </ComboboxGroup>
                      </Fragment>
                    ))}
                  </ComboboxListVirtualized>
                </Combobox>
                {reasoning || speed || props.access ? (
                  <div className="mx-1.5 shrink-0 border-border/70 border-t py-1" onKeyDownCapture={onRowsKeyDownCapture}>
                    {reasoning ? (
                      <ModelEffortSettingRow label="Reasoning">{selectRow(reasoning, reasoningSegments(reasoning))}</ModelEffortSettingRow>
                    ) : null}
                    {speed ? (
                      <ModelEffortSettingRow label="Speed">
                        {selectRow(
                          speed,
                          speedSegments(speed).map((segment) => ({ ...segment, icon: <ModelEffortSpeedIcon tier={segment.tier} /> })),
                        )}
                      </ModelEffortSettingRow>
                    ) : null}
                    {props.access ? <ModelEffortAccessRow access={props.access} /> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </TooltipProvider>
        </PopoverPopup>
      </Popover>
      {/* mod+shift+e clicks this (ChatComposer.openControl), opening the picker on the levels; never focused. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        data-composer-shortcut="composer.effort"
        onClick={openOnEffort}
      />
    </>
  );
});
